package acupoint.components

import antd.button.button
import antd.form.form
import antd.form.formItem
import antd.input.input
import antd.input.textArea
import antd.message.message
import antd.pageheader.pageHeader
import antd.select.SelectComponent
import antd.select.option
import antd.select.select
import com.jingfangpai.acupoint.model.AcupointDetail
import com.jingfangpai.common.orm.mapping.Acupoint
import kotlinext.js.Object
import kotlinx.browser.window
import react.*
import react.dom.div
import react.dom.jsStyle
import react.router.dom.useHistory
import setTitle
import acupoint.containers.AcupointEditPageContext
import com.jingfangpai.common.orm.mapping.enums.AcupointName
import urlBase

external interface AcupointEditProps : RProps {
    var id: String
}

val AcupointEdit = functionalComponent<AcupointEditProps> { props ->

    val ctx = useContext(AcupointEditPageContext)
    val history = useHistory()
    val (acupoint, setAcupoint) = useState(Acupoint())
    val (loading, setLoading) = useState(false)
    val (saving, setSaving) = useState(false)
    val (acupointNames, setAcupointNames) = useState(arrayOf<String>())

    useEffect(props.id) {
        // Fetch all acupoint names for the select dropdown
        ctx.listIdAndName({ resp ->
            if (resp.ok() && resp.data != null) {
                val names = resp.data!!.mapNotNull { it.name }.toTypedArray()
                setAcupointNames(names)
            }
        }, {})
        
        if (props.id != "new" && props.id.isNotBlank()) {
            setLoading(true)
            ctx.getDetail(props.id, { resp ->
                setLoading(false)
                if (resp.ok() && resp.data != null) {
                    setAcupoint(resp.data!!)
                    setTitle("编辑穴位 - ${resp.data!!.name?.getLiteral() ?: ""}")
                } else {
                    message.error("获取穴位失败")
                }
            }, {
                setLoading(false)
                message.error("获取穴位失败")
            })
        } else {
            setTitle("新增穴位")
        }
    }

    fun updateAcupoint(updater: (Acupoint) -> Unit) {
        val newAcupoint = Object.assign(Acupoint(), acupoint)
        updater(newAcupoint)
        setAcupoint(newAcupoint)
    }

    fun save() {
        setSaving(true)
        ctx.save(acupoint, { resp ->
            setSaving(false)
            if (resp.ok()) {
                message.success("保存成功")
                history.push("$urlBase/learning/acupoint/detail/${resp.data?.id ?: ""}")
            } else {
                message.error("保存失败：${resp.message}")
            }
        }, {
            setSaving(false)
            message.error("保存失败")
        })
    }

    div("AcupointEdit") {
        attrs.jsStyle = kotlinext.js.js {
            margin = "0 auto"
            maxWidth = "800px"
        }

        pageHeader {
            attrs {
                className = "acupoint-edit-page-header-responsive"
                onBack = {
                    window.history.back()
                }
                title = if (props.id == "new") "新增穴位" else "编辑穴位"
                subTitle = ""
            }
        }

        if (loading) {
            div {
                +"加载中..."
            }
        } else {
            form {
                attrs {
                    layout = "vertical"
                }

                formItem {
                    attrs {
                        label = "穴位名称"
                    }
                    if (props.id == "new") {
                        // For new acupoints, show selectable dropdown
                        select<String, SelectComponent<String>> {
                            attrs {
                                showSearch = true
                                style = kotlinext.js.js { width = 200 }
                                value = acupoint.name?.getLiteral()
                                placeholder = "请选择穴位名称"
                                disabled = saving
                                onChange = { value: String, option: dynamic ->
                                    updateAcupoint {
                                        it.name =  AcupointName.valueOf(value)
                                    }
                                }
                            }
                            acupointNames.forEach { name ->
                                option {
                                    attrs.value = name
                                    +name
                                }
                            }
                        }
                    } else {
                        // For existing acupoints, show disabled select
                        select<String, SelectComponent<String>> {
                            attrs {
                                disabled = true
                                style = kotlinext.js.js { width = 200 }
                                value = acupoint.name?.getLiteral()
                            }
                            option {
                                attrs.value = acupoint.name?.getLiteral() ?: ""
                                +(acupoint.name?.getLiteral() ?: "")
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "拼音"
                    }
                    input {
                        attrs {
                            value = acupoint.pinyin ?: ""
                            placeholder = "请输入拼音"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.pinyin = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "定位"
                    }
                    textArea {
                        attrs {
                            value = acupoint.location ?: ""
                            rows = 3
                            placeholder = "请输入穴位定位"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.location = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "主治"
                    }
                    textArea {
                        attrs {
                            value = acupoint.indications ?: ""
                            rows = 4
                            placeholder = "请输入主治内容"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.indications = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "操作"
                    }
                    textArea {
                        attrs {
                            value = acupoint.operation ?: ""
                            rows = 3
                            placeholder = "请输入操作方法"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.operation = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "经验"
                    }
                    textArea {
                        attrs {
                            value = acupoint.experience ?: ""
                            rows = 6
                            placeholder = "请输入临床经验"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.experience = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "处方"
                    }
                    textArea {
                        attrs {
                            value = acupoint.prescription ?: ""
                            rows = 6
                            placeholder = "请输入相关处方"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.prescription = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "鉴别"
                    }
                    textArea {
                        attrs {
                            value = acupoint.differentiation ?: ""
                            rows = 6
                            placeholder = "请输入鉴别要点"
                            disabled = saving
                            onChange = { event: dynamic ->
                                updateAcupoint { it.differentiation = event.target.value as String }
                            }
                        }
                    }
                }

                formItem {
                    button {
                        attrs {
                            type = "primary"
                            onClick = {
                                save()
                            }
                        }
                        attrs.loading = saving
                        +"保存"
                    }
                }
            }
        }
    }
}
